export const TICKET_VIEWED_EVENT = "ticket:viewed";
export const TICKETS_FORCE_REFRESH_EVENT = "tickets:force-refresh";
export const MESSAGES_FORCE_REFRESH_EVENT = "messages:force-refresh";
const TICKET_VIEWED_STATE_STORAGE_KEY = "ticket:viewed:state:v1";
const RECENT_VIEWED_TICKET_TTL_MS = 10 * 60 * 1000;

const inMemoryViewedState = {
  activeTicketId: null,
  activeTicketUuid: null,
  entriesById: {},
  entriesByUuid: {}
};

const canUseSessionStorage = () =>
  typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";

const sanitizeViewedEntry = (entry = {}) => {
  const viewedAt = Number(entry?.viewedAt || 0);
  if (!Number.isFinite(viewedAt) || viewedAt <= 0) {
    return null;
  }

  return {
    ticketId: Number(entry?.ticketId || 0) || null,
    ticketUuid: String(entry?.ticketUuid || "").trim() || null,
    viewedAt
  };
};

const pruneViewedEntries = (entries = {}) => {
  const now = Date.now();
  return Object.entries(entries).reduce((accumulator, [key, value]) => {
    const normalizedEntry = sanitizeViewedEntry(value);
    if (normalizedEntry && now - normalizedEntry.viewedAt <= RECENT_VIEWED_TICKET_TTL_MS) {
      accumulator[key] = normalizedEntry;
    }
    return accumulator;
  }, {});
};

const loadViewedState = () => {
  if (!canUseSessionStorage()) {
    return;
  }

  try {
    const rawValue = window.sessionStorage.getItem(TICKET_VIEWED_STATE_STORAGE_KEY);
    if (!rawValue) {
      return;
    }

    const parsedValue = JSON.parse(rawValue);
    inMemoryViewedState.activeTicketId = Number(parsedValue?.activeTicketId || 0) || null;
    inMemoryViewedState.activeTicketUuid =
      String(parsedValue?.activeTicketUuid || "").trim() || null;
    inMemoryViewedState.entriesById = pruneViewedEntries(parsedValue?.entriesById || {});
    inMemoryViewedState.entriesByUuid = pruneViewedEntries(parsedValue?.entriesByUuid || {});
  } catch (_error) {
    // ignore corrupted session payloads
  }
};

const persistViewedState = () => {
  if (!canUseSessionStorage()) {
    return;
  }

  try {
    window.sessionStorage.setItem(
      TICKET_VIEWED_STATE_STORAGE_KEY,
      JSON.stringify({
        activeTicketId: inMemoryViewedState.activeTicketId,
        activeTicketUuid: inMemoryViewedState.activeTicketUuid,
        entriesById: pruneViewedEntries(inMemoryViewedState.entriesById),
        entriesByUuid: pruneViewedEntries(inMemoryViewedState.entriesByUuid)
      })
    );
  } catch (_error) {
    // ignore storage write errors
  }
};

loadViewedState();

const normalizeViewedDetail = (detail = {}) => ({
  ticketId: Number(detail?.ticketId || 0) || null,
  ticketUuid: String(detail?.ticketUuid || "").trim() || null,
  viewedAt: Date.now()
});

const getViewedEntryForTicket = (detail = {}) => {
  const normalizedDetail = normalizeViewedDetail(detail);
  const entryById = normalizedDetail.ticketId
    ? sanitizeViewedEntry(inMemoryViewedState.entriesById[String(normalizedDetail.ticketId)])
    : null;
  const entryByUuid = normalizedDetail.ticketUuid
    ? sanitizeViewedEntry(inMemoryViewedState.entriesByUuid[String(normalizedDetail.ticketUuid)])
    : null;

  if (!entryById) {
    return entryByUuid;
  }

  if (!entryByUuid) {
    return entryById;
  }

  return entryById.viewedAt >= entryByUuid.viewedAt ? entryById : entryByUuid;
};

const parseTicketUpdateTimestamp = (ticket = {}) => {
  const candidates = [
    ticket?.lastMessageAt,
    ticket?.lastMessageCreatedAt,
    ticket?.updatedAt,
    ticket?.lastMessage?.createdAt,
    ticket?.ticket?.updatedAt
  ];

  for (const candidate of candidates) {
    const parsedTime = Date.parse(candidate);
    if (Number.isFinite(parsedTime)) {
      return parsedTime;
    }
  }

  return null;
};

export const isTicketViewedActive = (detail = {}) => {
  const normalizedDetail = normalizeViewedDetail(detail);

  if (
    normalizedDetail.ticketId &&
    inMemoryViewedState.activeTicketId &&
    normalizedDetail.ticketId === inMemoryViewedState.activeTicketId
  ) {
    return true;
  }

  if (
    normalizedDetail.ticketUuid &&
    inMemoryViewedState.activeTicketUuid &&
    normalizedDetail.ticketUuid === inMemoryViewedState.activeTicketUuid
  ) {
    return true;
  }

  return false;
};

export const shouldSuppressTicketUnread = (ticket = {}) => {
  const unreadMessages = Number(ticket?.unreadMessages || 0);
  if (unreadMessages <= 0) {
    return false;
  }

  if (
    isTicketViewedActive({
      ticketId: ticket?.id || ticket?.ticketId,
      ticketUuid: ticket?.uuid || ticket?.ticket?.uuid
    })
  ) {
    return true;
  }

  const viewedEntry = getViewedEntryForTicket({
    ticketId: ticket?.id || ticket?.ticketId,
    ticketUuid: ticket?.uuid || ticket?.ticket?.uuid
  });

  if (!viewedEntry) {
    return false;
  }

  const updatedAt = parseTicketUpdateTimestamp(ticket);
  if (Number.isFinite(updatedAt)) {
    return updatedAt <= viewedEntry.viewedAt + 1500;
  }

  return Date.now() - viewedEntry.viewedAt <= 30000;
};

export const notifyTicketViewed = (detail = {}) => {
  const payload = normalizeViewedDetail(detail);

  if (payload.ticketId) {
    inMemoryViewedState.entriesById[String(payload.ticketId)] = payload;
    inMemoryViewedState.activeTicketId = payload.ticketId;
  }

  if (payload.ticketUuid) {
    inMemoryViewedState.entriesByUuid[String(payload.ticketUuid)] = payload;
    inMemoryViewedState.activeTicketUuid = payload.ticketUuid;
  }

  persistViewedState();

  if (typeof window === "undefined") {
    return payload;
  }

  try {
    window.dispatchEvent(
      new CustomEvent(TICKET_VIEWED_EVENT, {
        detail: payload
      })
    );
  } catch (_error) {
    // ignore event dispatch errors
  }

  return payload;
};

export const clearTicketViewed = (detail = {}) => {
  const normalizedDetail = normalizeViewedDetail(detail);

  if (
    normalizedDetail.ticketId &&
    inMemoryViewedState.activeTicketId &&
    normalizedDetail.ticketId === inMemoryViewedState.activeTicketId
  ) {
    inMemoryViewedState.activeTicketId = null;
  }

  if (
    normalizedDetail.ticketUuid &&
    inMemoryViewedState.activeTicketUuid &&
    normalizedDetail.ticketUuid === inMemoryViewedState.activeTicketUuid
  ) {
    inMemoryViewedState.activeTicketUuid = null;
  }

  persistViewedState();
};

export const requestTicketsListRefresh = (detail = {}) => {
  const payload = {
    ticketId: Number(detail?.ticketId || 0) || null,
    ticketUuid: String(detail?.ticketUuid || "").trim() || null,
    reason: String(detail?.reason || "").trim() || "manual",
    requestedAt: Date.now()
  };

  if (typeof window === "undefined") {
    return payload;
  }

  try {
    window.dispatchEvent(
      new CustomEvent(TICKETS_FORCE_REFRESH_EVENT, {
        detail: payload
      })
    );
  } catch (_error) {
    // ignore event dispatch errors
  }

  return payload;
};

export const requestMessagesRefresh = (detail = {}) => {
  const payload = {
    ticketId: Number(detail?.ticketId || 0) || null,
    ticketUuid: String(detail?.ticketUuid || "").trim() || null,
    reason: String(detail?.reason || "").trim() || "manual",
    requestedAt: Date.now()
  };

  if (typeof window === "undefined") {
    return payload;
  }

  try {
    window.dispatchEvent(
      new CustomEvent(MESSAGES_FORCE_REFRESH_EVENT, {
        detail: payload
      })
    );
  } catch (_error) {
    // ignore event dispatch errors
  }

  return payload;
};
